'use client';

import type {
  ScheduledReportRule,
  UpdateApplicationRequestDto,
} from '@terra/shared-contracts-applications';
import { BodyDefault, MetaDefault } from '../../../components/typography';
import { ScheduledReportFrequencyDialog } from './ScheduledReportFrequencyDialog';
import { ScheduledReportRuleBlock } from './ScheduledReportRuleBlock';
import { SettingsBooleanControl } from '../SettingsBooleanControl';
import { SettingsCard } from '../SettingsCard';
import { SettingsRow } from '../SettingsRow';
import { Tooltip } from '../../../components/tooltip';
import {
  appendRulesForFrequencies,
  formatNextScheduledReportLabel,
  formatReportFrequencySummary,
  getAvailableScheduledReportFrequencies,
  getPersistableScheduledReportRules,
  getSoonestRuleTimezone,
  getSoonestScheduledReportOccurrence,
  areScheduledReportRulesPersistable,
  hasScheduledReportRuleFieldErrors,
  removeRuleById,
  replaceRuleById,
  sortScheduledReportRules,
} from './scheduled-report-rule.utils';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { HelpCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';

export type ScheduledReportsConfigurationCardProps = {
  enabled: boolean;
  rules: ScheduledReportRule[];
  nextScheduledReportAt?: Date | string | null;
  activeScheduledReportRuleCount?: number;
  disabled?: boolean;
  isUpdating?: boolean;
  onChange: (change: Partial<UpdateApplicationRequestDto>) => void;
  e2eAutomationIds?: {
    enabledSwitch?: string;
    frequencyEdit?: string;
    frequencyDialog?: string;
    frequencyAdd?: string;
    frequencyMonthly?: string;
    monthlyDaySelect?: string;
    hourEdit?: string;
    hourDialog?: string;
    hourSelect?: string;
    hourSave?: string;
    timezoneEdit?: string;
    timezoneDialog?: string;
    timezoneSelect?: string;
    timezoneSave?: string;
  };
};
export const ScheduledReportsConfigurationCard = ({
  enabled,
  rules,
  nextScheduledReportAt,
  activeScheduledReportRuleCount,
  disabled,
  isUpdating,
  onChange,
  e2eAutomationIds,
}: ScheduledReportsConfigurationCardProps) => {
  const t = useTranslations('designSystem.automation.scheduledReports');
  const tCommon = useTranslations('designSystem.common');
  const [isDraftEnabled, setIsDraftEnabled] = useState(false);
  const [isFrequencyDialogOpen, setIsFrequencyDialogOpen] = useState(false);
  const [localRules, setLocalRules] = useState<ScheduledReportRule[] | null>(
    null,
  );

  useEffect(() => {
    if (enabled) {
      setIsDraftEnabled(false);
    }
  }, [enabled]);
  useEffect(() => {
    setLocalRules((current) =>
      current?.some(hasScheduledReportRuleFieldErrors) ? current : null,
    );
  }, [rules]);
  const sortedRules = useMemo(() => sortScheduledReportRules(rules), [rules]);
  const displayRules = useMemo(
    () => sortScheduledReportRules(localRules ?? sortedRules),
    [localRules, sortedRules],
  );
  const isPanelOpen = enabled || isDraftEnabled;
  const frequencySummary =
    formatReportFrequencySummary({
      rules: displayRules,
      getFrequencyLabel: (frequency) => t(`frequencies.${frequency}`),
    }) ?? t('frequencyNone');
  const nextOccurrence = useMemo(
    () => getSoonestScheduledReportOccurrence(displayRules),
    [displayRules],
  );
  const nextReportLabel =
    formatNextScheduledReportLabel({
      nextScheduledReportAt: nextOccurrence?.runAt ?? nextScheduledReportAt,
      timezone:
        nextOccurrence?.timezone ?? getSoonestRuleTimezone(displayRules),
    }) ?? t('notScheduled');
  const activeCount =
    activeScheduledReportRuleCount ??
    (enabled ? getPersistableScheduledReportRules(displayRules).length : 0);
  const isControlDisabled = Boolean(disabled);
  const availableFrequencies = useMemo(
    () => getAvailableScheduledReportFrequencies(displayRules),
    [displayRules],
  );
  const canEditFrequencies =
    !isControlDisabled && availableFrequencies.length > 0;
  const persistRulesIfValid = (nextRules: ScheduledReportRule[]) => {
    if (nextRules.length === 0) {
      setLocalRules(null);
      setIsDraftEnabled(false);
      if (enabled) {
        onChange({
          scheduledReportsEnabled: false,
          scheduledReportRules: [],
        });
      }

      return;
    }

    const persistableRules = getPersistableScheduledReportRules(nextRules);

    setLocalRules(nextRules);
    if (!enabled) {
      setIsDraftEnabled(true);
    }

    if (persistableRules.length === 0) {
      if (enabled) {
        onChange({
          scheduledReportsEnabled: false,
          scheduledReportRules: [],
        });
      }

      return;
    }

    onChange({
      scheduledReportsEnabled: true,
      scheduledReportRules: persistableRules,
    });
  };

  const handleEnabledChange = (checked: boolean) => {
    if (!checked) {
      setIsDraftEnabled(false);
      setLocalRules(null);
      if (enabled) {
        onChange({
          scheduledReportsEnabled: false,
          scheduledReportRules: [],
        });
      }

      return;
    }

    if (areScheduledReportRulesPersistable(sortedRules)) {
      setIsDraftEnabled(true);
      onChange({
        scheduledReportsEnabled: true,
        scheduledReportRules: sortedRules,
      });

      return;
    }

    setIsDraftEnabled(true);
    setLocalRules(null);
  };

  const handleAddFrequencies = (
    frequencies: ScheduledReportRule['frequency'][],
  ) => {
    persistRulesIfValid(
      appendRulesForFrequencies({
        rules: displayRules,
        frequencies,
      }),
    );
  };

  const handleRuleChange = (nextRule: ScheduledReportRule) => {
    persistRulesIfValid(
      replaceRuleById({
        rules: displayRules,
        nextRule,
      }),
    );
  };

  const handleRuleRemove = (ruleId: string) => {
    persistRulesIfValid(
      removeRuleById({
        rules: displayRules,
        ruleId,
      }),
    );
  };

  return (
    <>
      <SettingsCard>
        <VStack alignItems="stretch" gap={4} w="full">
          <SettingsRow
            label={t('title')}
            tooltip={t('tooltip')}
            control={
              <SettingsBooleanControl
                checked={isPanelOpen}
                label={isPanelOpen ? tCommon('yes') : tCommon('no')}
                disabled={isControlDisabled}
                onCheckedChange={(details) =>
                  handleEnabledChange(details.checked)
                }
                testId={e2eAutomationIds?.enabledSwitch}
              />
            }
          />

          {isPanelOpen ? (
            <>
              <Box h="1px" bg="border.default" />

              <SettingsRow
                label={
                  <BodyDefault color="fg.neutral.default">
                    {t('reportFrequency')}
                  </BodyDefault>
                }
                value={frequencySummary}
                action={{
                  label: tCommon('edit'),
                  onClick: () => setIsFrequencyDialogOpen(true),
                  disabled: !canEditFrequencies,
                  testId: e2eAutomationIds?.frequencyEdit,
                }}
                isReadOnly={disabled}
              />

              <VStack alignItems="stretch" gap={0} w="full">
                {displayRules.map((rule) => (
                  <Box
                    key={rule.id}
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="border.default"
                    borderRadius="md"
                    m="3px"
                    p={3}
                  >
                    <ScheduledReportRuleBlock
                      rule={rule}
                      disabled={disabled}
                      isUpdating={isUpdating}
                      canRemove
                      onChange={handleRuleChange}
                      onRemove={() => handleRuleRemove(rule.id)}
                      e2eAutomationIds={e2eAutomationIds}
                    />
                  </Box>
                ))}
              </VStack>

              <Box h="1px" bg="border.default" />
              <HStack
                w="full"
                justifyContent="space-between"
                alignItems="center"
                gap={4}
              >
                <HStack gap={1} minW={0}>
                  <MetaDefault color="fg.neutral.muted">
                    {t('nextReport', { date: nextReportLabel })}
                  </MetaDefault>
                  <Tooltip
                    title={t('nextReportTooltip')}
                    positioning={{ strategy: 'fixed' }}
                  >
                    <Box display="flex" color="fg.neutral.muted">
                      <HelpCircleIcon size={16} />
                    </Box>
                  </Tooltip>
                </HStack>
                <MetaDefault color="fg.neutral.muted" flexShrink={0}>
                  {t('activeFrequencies', { count: activeCount })}
                </MetaDefault>
              </HStack>
            </>
          ) : null}
        </VStack>
      </SettingsCard>

      <ScheduledReportFrequencyDialog
        open={isFrequencyDialogOpen}
        disabled={isControlDisabled}
        availableFrequencies={availableFrequencies}
        onOpenChange={setIsFrequencyDialogOpen}
        onAdd={handleAddFrequencies}
        contentAutomationId={e2eAutomationIds?.frequencyDialog}
        submitAutomationId={e2eAutomationIds?.frequencyAdd}
        frequencyOptionAutomationIds={
          e2eAutomationIds?.frequencyMonthly
            ? { MONTHLY: e2eAutomationIds.frequencyMonthly }
            : undefined
        }
      />
    </>
  );
};
